import { useEffect, useRef } from 'react';

/**
 * How long a dashboard document is trusted without re-asking, when the only thing that changed is that the
 * user came back to the tab.
 *
 * Matched to the dashboard service's own published-version pointer TTL (60s): re-asking faster than the
 * server re-reads which component version is published cannot surface anything newer, so it would be a
 * request that can only ever answer 304.
 */
export const REVEAL_REVALIDATE_MIN_INTERVAL_MS = 60_000;

/**
 * The whole decision, as a pure function: should this reveal spend a request?
 *
 * Extracted so it is testable in this package's node test environment rather than through a fake DOM — a
 * DOM-shaped test here would exercise a stub of `visibilitychange` and prove less than it appears to. The
 * hook below is a four-line wrapper: subscribe, ask this, remember when it last said yes.
 */
export function shouldRevalidateOnReveal(
  visibilityState: string,
  nowMs: number,
  lastRevalidatedAtMs: number,
): boolean {
  // The event fires in both directions; only BECOMING visible is a moment the answer can matter to anyone.
  if (visibilityState !== 'visible') {
    return false;
  }
  // A burst (a mounted host holds several layers, and a tab-switch reveals them together) collapses to one.
  return nowMs - lastRevalidatedAtMs >= REVEAL_REVALIDATE_MIN_INTERVAL_MS;
}

/**
 * Re-validate a mounted dashboard surface when the tab becomes visible again.
 *
 * ## Why this exists
 *
 * A dashboard surface the user has visited stays MOUNTED (see DashboardHost — hidden layers keep their
 * iframes warm), and the iframe only re-requests when its cache-buster changes. The one thing that changes
 * it is the `dashboard-changed` duplexer signal, which fires on a write to THIS site's dashboards.
 *
 * A component-library publish is not that. It changes what every installing site renders without touching
 * any site's dashboard rows, so it emits no per-site signal — and fanning one out to every site holding that
 * component would be a flood, not a fix. The result was that an already-open web surface kept serving the
 * previous composition until a full page load, which also quietly blunted "re-point `defaultVersion` and
 * every site reverts" as a rollback lever. Native was never affected: it re-fetches unconditionally on every
 * screen entry.
 *
 * So the surface re-asks on its own, and the ETag makes that nearly free: the document is served
 * `private, must-revalidate` with a content hash, so an unchanged document answers 304 with no body.
 *
 * Throttled because a mounted-surface host can hold several layers and a tab-switch would otherwise fire one
 * request per layer per switch. The first reveal after the interval re-asks; the rest are skipped.
 *
 * Deliberately NOT a poll. A timer would re-request for users who are looking at something else, which is the
 * cost this is trying to avoid; a reveal is the moment the answer can actually matter to someone.
 */
export function useRevalidateOnReveal(revalidate: () => void): void {
  const revalidateRef = useRef(revalidate);
  revalidateRef.current = revalidate;
  // Seeded at mount: the document was just fetched, so it does not need re-asking yet. Seeding at 0 would
  // make the FIRST reveal always re-request, which for a user tabbing straight back is pure waste.
  const lastAt = useRef(Date.now());

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const onVisibility = () => {
      const now = Date.now();
      if (!shouldRevalidateOnReveal(document.visibilityState, now, lastAt.current)) {
        return;
      }
      lastAt.current = now;
      revalidateRef.current();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
}
